import React from "react";
import Modal from "@cloudscape-design/components/modal";
import Form from "@cloudscape-design/components/form";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Checkbox from "@cloudscape-design/components/checkbox";
import Container from "@cloudscape-design/components/container";
import Alert from "@cloudscape-design/components/alert";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {  prizeEditModalStore } from "../stores/prizeEditModal";
import Header from "@cloudscape-design/components/header";
import Grid from "@cloudscape-design/components/grid";
import DatePicker from "@cloudscape-design/components/date-picker";
import { gameStore } from "../stores/games";
import { RISK_MODEL_OPTIONS, getRiskModelSelectedOptions } from "../helpers/riskModelOptions";
import { PARTNER_PAYMENT_TYPE_OPTIONS, hasValidItemUidCondition } from "../helpers/cohortConditions";
import { formatChestPrizeType, getChestBannerUrl } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const getChestPrizeIdFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return null;
	}

	return parsed?.chestPrizeId || null;
};

const getChestTypeFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return "";
	}

	return parsed?.type || "";
};

const normalizeChestPrizeValue = (value) => {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeChestPrizeValue(entry));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((accumulator, key) => {
				accumulator[key] = normalizeChestPrizeValue(value[key]);
				return accumulator;
			}, {});
	}

	return value;
};

const getChestPrizeSignature = ({ type, rewardConditions }) =>
	JSON.stringify({
		type: type || "",
		rewardConditions: normalizeChestPrizeValue(getChestRewardsPayload(rewardConditions)),
	});

const findMatchingChestPrize = (items = [], { type, rewardConditions }) => {
	const targetSignature = getChestPrizeSignature({ type, rewardConditions });

	return (
		(items || []).find(
			(item) =>
				getChestPrizeSignature({
					type: item.type,
					rewardConditions: item.rewardConditions,
				}) === targetSignature
		) || null
	);
};

const buildChestPrizeOption = (item) => ({
	value: String(item.id),
	label: `${item.name} (${item.id})`,
	description: formatChestPrizeType(item.type),
	chestPrize: item,
});

const FREESPINS_PROVIDER_OPTIONS = [{ label: "Endorphina", value: "endorphina" }];

class PrizeEditModal extends React.Component {
	state = {
		imageError: "",
		levelError: "",
		cdnUrl: "",
		cohortGroups: [],
		newConditionByGroupId: {},
	};

	getGroupKeyByModule = (module) => {
		if (module === "deposit") return "depositFilters";
		if (module === "withdrawal") return "withdrawalFilters";
		if (module === "registration") return "registrationFilters";
		return "profileFilters";
	};

	getCohortModuleOptions = () => [
		{ value: "deposit", label: "Deposit" },
		{ value: "withdrawal", label: "Withdrawal" },
		{ value: "registration", label: "Registration" },
		{ value: "profile", label: "Profile" },
	];

	getLogicalOperatorOptions = () => [
		{ value: "AND", label: "AND" },
		{ value: "OR", label: "OR" },
	];

	getCohortFieldOptions = () => [
		{ value: "id", label: "Id", type: "string" },
		{ value: "risk_model", label: "Risk model", type: "multiselect", specialType: "riskModel" },
		{ value: "verify", label: "Verify", type: "select", specialType: "boolean" },
		{ value: "sweepstakes_count", label: "Sweepstakes_count", type: "count" },
		{ value: "email", label: "Email", type: "select", specialType: "boolean" },
		{ value: "phone", label: "Phone", type: "select", specialType: "boolean" },
		{ value: "tg", label: "Telegram", type: "select", specialType: "boolean" },
		{ value: "partnerName", label: "Partner Name", type: "string" },
		{ value: "paymentType", label: "Payment Type", type: "select", specialType: "partnerPaymentType" },
		{ value: "trafficType", label: "Traffic Type", type: "string" },

		{ value: "deposit_date", label: "Deposit date", type: "count" },
		{ value: "deposit_date_crypto", label: "Deposit crypto date", type: "count" },
		{ value: "deposit_date_fiat", label: "Deposit fiat date", type: "count" },
		{ value: "deposit_count", label: "Deposit count", type: "count" },
		{ value: "deposit_count_fiat", label: "Deposit fiat count", type: "count" },
		{ value: "deposit_count_crypto", label: "Deposit crypto count", type: "count" },
		{ value: "deposit_from", label: "Deposit from", type: "date" },
		{ value: "deposit_to", label: "Deposit to", type: "date" },
		{ value: "deposit_crypto_from", label: "Deposit crypto from", type: "date" },
		{ value: "deposit_crypto_to", label: "Deposit crypto to", type: "date" },
		{ value: "deposit_fiat_from", label: "Deposit fiat from", type: "date" },
		{ value: "deposit_fiat_to", label: "Deposit fiat to", type: "date" },
		{ value: "deposit_amount_to", label: "Deposit amount to", type: "count" },
		{ value: "deposit_amount_from", label: "Deposit amount from", type: "count" },
		{ value: "deposit_amount_crypto_to", label: "Deposit amount crypto to", type: "count" },
		{ value: "deposit_amount_crypto_from", label: "Deposit amount crypto from", type: "count" },
		{ value: "deposit_amount_fiat_to", label: "Deposit amount fiat to", type: "count" },
		{ value: "deposit_amount_fiat_from", label: "Deposit amount fiat from", type: "count" },
		{ value: "deposit_type", label: "Deposit type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "withdrawal_date", label: "Withdrawal date", type: "count" },
		{ value: "withdrawal_date_crypto", label: "Withdrawal crypto date", type: "count" },
		{ value: "withdrawal_date_fiat", label: "Withdrawal fiat date", type: "count" },
		{ value: "withdrawal_count", label: "Withdrawal count", type: "count" },
		{ value: "withdrawal_count_fiat", label: "Withdrawal fiat count", type: "count" },
		{ value: "withdrawal_count_crypto", label: "Withdrawal crypto count", type: "count" },
		{ value: "withdrawal_from", label: "Withdrawal from", type: "date" },
		{ value: "withdrawal_to", label: "Withdrawal to", type: "date" },
		{ value: "withdrawal_crypto_from", label: "Withdrawal crypto from", type: "date" },
		{ value: "withdrawal_crypto_to", label: "Withdrawal crypto to", type: "date" },
		{ value: "withdrawal_fiat_from", label: "Withdrawal fiat from", type: "date" },
		{ value: "withdrawal_fiat_to", label: "Withdrawal fiat to", type: "date" },
		{ value: "withdrawal_amount_to", label: "Withdrawal amount to", type: "count" },
		{ value: "withdrawal_amount_from", label: "Withdrawal amount from", type: "count" },
		{ value: "withdrawal_amount_crypto_to", label: "Withdrawal amount crypto to", type: "count" },
		{ value: "withdrawal_amount_crypto_from", label: "Withdrawal amount crypto from", type: "count" },
		{ value: "withdrawal_amount_fiat_to", label: "Withdrawal amount fiat to", type: "count" },
		{ value: "withdrawal_amount_fiat_from", label: "Withdrawal amount fiat from", type: "count" },
		{ value: "withdrawal_type", label: "Withdrawal type", type: "select", specialType: "cryptoOrFiat" },

		{ value: "spins", label: "Spins", type: "count" },
		{ value: "lvl_club", label: "Club", type: "count" },
		{ value: "referer", label: "Referer", type: "count" },
		{ value: "balance", label: "Balance", type: "count" },
		{ value: "registration_from", label: "Registration from", type: "date" },
		{ value: "registration_to", label: "Registration to", type: "date" },
		{ value: "registration_date", label: "Registration user days", type: "count" },
	];

	getCohortFieldOptionsByModule = (module) => {
		const fields = this.getCohortFieldOptions();

		if (module === "deposit") return fields.filter((f) => f.value.startsWith("deposit_"));
		if (module === "withdrawal") return fields.filter((f) => f.value.startsWith("withdrawal_"));
		if (module === "registration") return fields.filter((f) => f.value.startsWith("registration_"));

		return fields.filter(
			(f) =>
				!f.value.startsWith("deposit_") &&
				!f.value.startsWith("withdrawal_") &&
				!f.value.startsWith("registration_")
		);
	};

	getCohortStateKeys = () => ({
		conditionsKey: "conditions",
		groupsKey: "cohortGroups",
		newConditionKey: "newConditionByGroupId",
	});

	updateNewCondition = (groupId, patch, cohortType = "visibility") => {
		const { newConditionKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => ({
			[newConditionKey]: {
				...(prev[newConditionKey] || {}),
				[groupId]: {
					...(prev[newConditionKey]?.[groupId] || {}),
					...patch,
				},
			},
		}));
	};

	resetNewCondition = (groupId, cohortType = "visibility") => {
		const { newConditionKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => ({
			[newConditionKey]: {
				...(prev[newConditionKey] || {}),
				[groupId]: {},
			},
		}));
	};

	isConditionValueEmpty = (value) => value === null || value === "" || (Array.isArray(value) && value.length === 0);

	normalizeGroupConditions = (conditions, groupId) => {
		const groupConditions = (conditions || [])
			.filter((c) => c.groupId === groupId)
			.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		if (!groupConditions.length) return conditions;

		const firstId = groupConditions[0].id;
		return (conditions || []).map((c) => {
			if (c.groupId !== groupId) return c;
			if (c.id === firstId) return { ...c, logicalOperator: null };
			if (!c.logicalOperator) return { ...c, logicalOperator: "AND" };
			return c;
		});
	};

	normalizeCohortConditions = (rawConditions) => {
		const fieldMetaByValue = new Map(
			this.getCohortFieldOptions().map((f) => [f.value, f])
		);

		const deriveModule = (field) => {
			if (!field) return "profile";
			if (field.startsWith("deposit_")) return "deposit";
			if (field.startsWith("withdrawal_")) return "withdrawal";
			if (field.startsWith("registration_")) return "registration";
			return "profile";
		};

		const conditions = Array.isArray(rawConditions) ? rawConditions.filter(Boolean) : [];
		if (!conditions.length) {
			const groupId = crypto.randomUUID();
			return {
				groups: [
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator: null,
					},
				],
				conditions: [],
			};
		}

		const hasAllGroupIds = conditions.every((c) => c && typeof c === "object" && c.groupId);

		if (!hasAllGroupIds) {
			const hasAnyGroupKey = conditions.some((c) => c && typeof c === "object" && c.groupKey);

			if (hasAnyGroupKey) {
				const groupsByKey = new Map();
				const groupKeysOrder = [];

				conditions.forEach((c) => {
					const field = c.field || c.fieldLabel || "";
					const module = deriveModule(field);
					const groupKey = c.groupKey || this.getGroupKeyByModule(module);
					if (groupsByKey.has(groupKey)) return;

					const groupId = crypto.randomUUID();
					const groupLogicalOperator = groupKeysOrder.length === 0 ? null : "AND";

					groupsByKey.set(groupKey, { groupId, module, groupKey, groupLogicalOperator });
					groupKeysOrder.push(groupKey);
				});

				const groups = groupKeysOrder.map((groupKey, idx) => {
					const g = groupsByKey.get(groupKey);
					return {
						groupId: g.groupId,
						module: g.module || "deposit",
						groupKey: g.groupKey,
						groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
					};
				});

				const groupMetaByKey = new Map(
					groups.map((g, idx) => [g.groupKey, { ...g, groupOrder: idx }])
				);
				const conditionOrderByGroupId = new Map();

				let normalizedConditions = conditions
					.map((c) => {
						const field = c.field || c.fieldLabel || "";
						const module = deriveModule(field);
						const groupKey = c.groupKey || this.getGroupKeyByModule(module);
						const meta = groupMetaByKey.get(groupKey);
						if (!meta) return null;

						const prevOrder = conditionOrderByGroupId.get(meta.groupId) ?? -1;
						const conditionOrder = prevOrder + 1;
						conditionOrderByGroupId.set(meta.groupId, conditionOrder);

						const fm = fieldMetaByValue.get(field);

						return {
							...c,
							id: c.id || crypto.randomUUID(),
							field,
							fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
							conditionalType: c.conditionalType || fm?.type,
							specialType:
								typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
							groupId: meta.groupId,
							module: meta.module,
							groupKey: meta.groupKey,
							groupOrder: meta.groupOrder,
							groupLogicalOperator:
								meta.groupOrder === 0 ? null : meta.groupLogicalOperator || "AND",
							conditionOrder:
								typeof c.conditionOrder === "number" ? c.conditionOrder : conditionOrder,
							logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
						};
					})
					.filter(Boolean);

				groups.forEach((g) => {
					normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
				});

				return { groups, conditions: normalizedConditions };
			}

			const groupsByModule = new Map();
			const modulesOrder = [];

			conditions.forEach((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				if (groupsByModule.has(module)) return;

				const groupId = crypto.randomUUID();
				const groupLogicalOperator = modulesOrder.length === 0 ? null : "AND";

				groupsByModule.set(module, {
					groupId,
					module,
					groupKey: this.getGroupKeyByModule(module),
					groupLogicalOperator,
				});
				modulesOrder.push(module);
			});

			const groups = modulesOrder.map((module, idx) => {
				const g = groupsByModule.get(module);
				return {
					...g,
					groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
				};
			});

			const groupMetaByModule = new Map(
				groups.map((g, idx) => [g.module, { ...g, groupOrder: idx }])
			);
			const conditionOrderByGroupId = new Map();

			let normalizedConditions = conditions.map((c) => {
				const module = deriveModule(c.field || c.fieldLabel || "");
				const meta = groupMetaByModule.get(module);

				const prevOrder = conditionOrderByGroupId.get(meta.groupId) ?? -1;
				const conditionOrder = prevOrder + 1;
				conditionOrderByGroupId.set(meta.groupId, conditionOrder);

				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: meta.groupId,
					module: meta.module,
					groupKey: c.groupKey || meta.groupKey,
					groupOrder: meta.groupOrder,
					groupLogicalOperator: meta.groupOrder === 0 ? null : meta.groupLogicalOperator || "AND",
					conditionOrder: typeof c.conditionOrder === "number" ? c.conditionOrder : conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

			groups.forEach((g) => {
				normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
			});

			return { groups, conditions: normalizedConditions };
		}

		const groupMeta = new Map();
		const firstSeenOrder = new Map();

		conditions.forEach((c, idx) => {
			if (!c || typeof c !== "object") return;
			if (!firstSeenOrder.has(c.groupId)) firstSeenOrder.set(c.groupId, idx);

			const field = c.field || c.fieldLabel || "";
			const module = c.module || deriveModule(field);

			if (!groupMeta.has(c.groupId)) {
				groupMeta.set(c.groupId, {
					groupId: c.groupId,
					module,
					groupKey: c.groupKey || this.getGroupKeyByModule(module),
					groupLogicalOperator: c.groupLogicalOperator || null,
					groupOrder: typeof c.groupOrder === "number" ? c.groupOrder : firstSeenOrder.get(c.groupId),
				});
			}

			const meta = groupMeta.get(c.groupId);
			if (!meta.module) meta.module = module;
			if (typeof c.groupOrder === "number") meta.groupOrder = c.groupOrder;
			if (c.groupKey) meta.groupKey = c.groupKey;
			if (c.groupLogicalOperator) meta.groupLogicalOperator = c.groupLogicalOperator;
		});

		const groups = [...groupMeta.values()]
			.sort((a, b) => (a.groupOrder ?? 0) - (b.groupOrder ?? 0))
			.map((g, idx) => ({
				groupId: g.groupId,
				module: g.module || "deposit",
				groupKey: g.groupKey || this.getGroupKeyByModule(g.module || "deposit"),
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const indexByGroupId = new Map(groups.map((g, idx) => [g.groupId, idx]));
		const nextOrderByGroupId = new Map();

		let normalizedConditions = conditions
			.filter((c) => c && typeof c === "object" && indexByGroupId.has(c.groupId))
			.map((c) => {
				const groupIndex = indexByGroupId.get(c.groupId);
				const group = groups[groupIndex];

				const prevOrder = nextOrderByGroupId.get(c.groupId) ?? -1;
				const conditionOrder =
					typeof c.conditionOrder === "number" ? c.conditionOrder : prevOrder + 1;
				nextOrderByGroupId.set(c.groupId, Math.max(prevOrder, conditionOrder));

				const field = c.field || c.fieldLabel || "";
				const fm = fieldMetaByValue.get(field);

				return {
					...c,
					id: c.id || crypto.randomUUID(),
					field,
					fieldLabel: c.fieldLabel || fm?.label || c.field || field || "",
					conditionalType: c.conditionalType || fm?.type,
					specialType: typeof c.specialType !== "undefined" ? c.specialType : fm?.specialType,
					groupId: group.groupId,
					module: group.module,
					groupKey: c.groupKey || group.groupKey || this.getGroupKeyByModule(group.module),
					groupOrder: groupIndex,
					groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
					conditionOrder,
					logicalOperator: conditionOrder === 0 ? null : c.logicalOperator || "AND",
				};
			});

		groups.forEach((g) => {
			normalizedConditions = this.normalizeGroupConditions(normalizedConditions, g.groupId);
		});

		return { groups, conditions: normalizedConditions };
	};

	addCohortGroup = (cohortType = "visibility") => {
		const groupId = crypto.randomUUID();
		const { groupsKey } = this.getCohortStateKeys(cohortType);

		this.setState((prev) => {
			const groups = [...(prev[groupsKey] || [])];
			const groupLogicalOperator = groups.length === 0 ? null : "AND";

			return {
				[groupsKey]: [
					...groups,
					{
						groupId,
						module: "deposit",
						groupKey: "depositFilters",
						groupLogicalOperator,
					},
				],
			};
		});
	};

	updateCohortGroup = (groupId, patch, cohortType = "visibility") => {
		const { conditionsKey, groupsKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || []).map((g) =>
			g.groupId === groupId ? { ...g, ...patch } : g
		);

		this.setState({ [groupsKey]: nextGroups });

		const conditions = this.props.prizeEditModal[conditionsKey] || [];
		const updatedConditions = conditions.map((c) =>
			c.groupId === groupId
				? {
						...c,
						...(typeof patch.module !== "undefined" ? { module: patch.module } : {}),
						...(typeof patch.module !== "undefined"
							? { groupKey: this.getGroupKeyByModule(patch.module) }
							: {}),
						...(typeof patch.groupLogicalOperator !== "undefined"
							? { groupLogicalOperator: patch.groupLogicalOperator }
							: {}),
				  }
				: c
		);

		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			[conditionsKey]: updatedConditions,
		});
	};

	changeCohortGroupModule = (groupId, module, cohortType = "visibility") => {
		const { conditionsKey, groupsKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || []).map((g) =>
			g.groupId === groupId ? { ...g, module, groupKey: this.getGroupKeyByModule(module) } : g
		);

		this.setState({ [groupsKey]: nextGroups });

		const nextConditions = (this.props.prizeEditModal[conditionsKey] || []).filter(
			(c) => c.groupId !== groupId
		);

		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			[conditionsKey]: nextConditions,
		});

		this.resetNewCondition(groupId, cohortType);
	};

	removeCohortGroup = (groupId, cohortType = "visibility") => {
		const { conditionsKey, groupsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const nextGroups = (this.state[groupsKey] || [])
			.filter((g) => g.groupId !== groupId)
			.map((g, idx) => ({
				...g,
				groupLogicalOperator: idx === 0 ? null : g.groupLogicalOperator || "AND",
			}));

		const groupIndexById = new Map(nextGroups.map((g, idx) => [g.groupId, idx]));

		const nextConditions = (this.props.prizeEditModal[conditionsKey] || [])
			.filter((c) => c.groupId !== groupId)
			.filter((c) => groupIndexById.has(c.groupId))
			.map((c) => {
				const groupOrder = groupIndexById.get(c.groupId);
				const group = nextGroups[groupOrder];

				return {
					...c,
					module: group.module,
					groupOrder,
					groupLogicalOperator: groupOrder === 0 ? null : group.groupLogicalOperator || "AND",
				};
		});

		this.setState((prev) => {
			const nextNewConditionByGroupId = { ...(prev[newConditionKey] || {}) };
			delete nextNewConditionByGroupId[groupId];

			return {
				[groupsKey]: nextGroups,
				[newConditionKey]: nextNewConditionByGroupId,
			};
		});

		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			[conditionsKey]: nextConditions,
		});
	};

	addCohortCondition = (group, groupIndex, cohortType = "visibility") => {
		const { conditionsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const conditions = this.props.prizeEditModal[conditionsKey] || [];
		const groupConditions = conditions
			.filter((c) => c.groupId === group.groupId)
			.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

		const newCondition = this.state[newConditionKey]?.[group.groupId] || {};
		if (!newCondition.field || this.isConditionValueEmpty(newCondition.value)) return;

		let value = newCondition.value;
		if (newCondition.conditionalType === "select") {
			value = newCondition.value.value || newCondition.value;
		} else if (newCondition.conditionalType === "boolean") {
			value = newCondition.value ? 1 : 0;
		}

		const nextOrder = groupConditions.length
			? Math.max(...groupConditions.map((c) => c.conditionOrder ?? 0)) + 1
			: 0;

		const logicalOperator = groupConditions.length
			? newCondition.logicalOperator || "AND"
			: null;

		const item = {
			...newCondition,
			id: crypto.randomUUID(),
			value,
			logicalOperator,
			conditionOrder: nextOrder,
			groupId: group.groupId,
			module: group.module,
			groupKey: group.groupKey || this.getGroupKeyByModule(group.module),
			groupOrder: groupIndex,
			groupLogicalOperator: groupIndex === 0 ? null : group.groupLogicalOperator || "AND",
		};

		const nextConditions = this.normalizeGroupConditions(
			[...conditions, item],
			group.groupId
		);

		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			[conditionsKey]: nextConditions,
		});
		this.resetNewCondition(group.groupId, cohortType);
	};

	loadCdnConfig = async () => {
		try {
			const response = await this.props.Interface.request(
				"get",
				{ class: "data", module: "personalPromotions", method: "getConfig" },
				{}
			);

			if (response?.cdn) {
				this.setState({ cdnUrl: response.cdn });
			}
		} catch (error) {
			console.error("Failed to load CDN config", error);
		}
	};

	initModal = async () => {
		try {
			const normalizedCohort = this.normalizeCohortConditions(this.props.prizeEditModal.conditions);

			this.setState({
				cohortGroups: normalizedCohort.groups,
				newConditionByGroupId: {},
			});

			this.props.setPrizeEditModal(prev => ({
				...prev,
				loaded: false,
				conditions: normalizedCohort.conditions,
			}));


			let providerSelected = null;
			let gameSelected = null;
			let availableGames = [];
			let betAmount = "";
			let expiresAt = "";
			let chestRewards = [];
			let chestType = "";
			let chestPrizeId = null;
			const rewardTypeValue = this.props.prizeEditModal.rewardType?.value;
			const rewardConditions = this.parseRewardConditions(this.props.prizeEditModal.rewardConditions);
			const hidden = Boolean(rewardConditions.hidden);
			const showInBanner =
				Number(this.props.prizeEditModal.showInBanner) === 1 ||
				Boolean(rewardConditions.showInBanner);

			if (rewardTypeValue === "freespins") {
				providerSelected = FREESPINS_PROVIDER_OPTIONS[0];
				availableGames = this.getAvailableEndorphinaGames();

				if (!availableGames.length) {
					this.props.Interface.addNoty("Error", "Please reload page game state not loading");
				}

				if (rewardConditions?.gameId && availableGames.length > 0) {
					const foundGame = availableGames.find((game) => game.id === Number(rewardConditions.gameId));
					if (foundGame) {
						gameSelected = {
							label: foundGame.description,
							value: foundGame.description,
							gameId: foundGame.id,
							game: foundGame,
						};
					}
				}

				betAmount = Number(rewardConditions.betAmount) || "";
				expiresAt = rewardConditions.expiresAt || "";
			} else if (rewardTypeValue === "chest") {
				chestRewards = getChestRewardsPayload(this.props.prizeEditModal.rewardConditions);
				chestPrizeId = getChestPrizeIdFromRewardConditions(this.props.prizeEditModal.rewardConditions);
				chestType =
					this.props.prizeEditModal.type || getChestTypeFromRewardConditions(this.props.prizeEditModal.rewardConditions);
			}



			this.props.setPrizeEditModal(prev => ({
				...prev,
				availableGames,
				providerSelected,
				gameSelected,
				betAmount,
				expiresAt,
				chestRewards,
				chestType,
				chestPrizeId,
				hidden,
				showInBanner,
				loaded: true,
			}));

			if (rewardTypeValue === "chest") {
				this.loadChestPrizeOptions({ matchCurrent: true });
			}
		} catch (e) {
			console.error("Init failed", e);
			this.props.setPrizeEditModal(prev => ({ ...prev, loaded: true }));
		}
	};

	componentDidMount() {
		this.loadCdnConfig();
		this.initModal();
	}

	componentDidUpdate(prevProps) {
		const wasHidden = !prevProps.prizeEditModal.show && this.props.prizeEditModal.show;
		const changedId = prevProps.prizeEditModal.id !== this.props.prizeEditModal.id;

		if ((wasHidden || changedId) && this.props.prizeEditModal.show) {
			this.initModal();
		}
	}

	componentWillUnmount() {
		if (this.props.prizeEditModal.image) {
			URL.revokeObjectURL(this.props.prizeEditModal.image);
		}
	}
	
	onDismiss = () => {
		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			name: "",
			description: "",
			category_id: null,
			rewardValue: "",
			image: null,
			cycle: null,
			cycleUnit: null,
			cycleTimer: null,
			conditions: [],
			buttonText: "",
			link: "",
			active: "",
			categories: [],
			count: "",
			fakeWinsPerMonth: "",
			chestRewards: [],
			chestType: "",
			chestPrizeId: null,
			chestPrizeSelected: null,
			chestPrizeOptions: [],
			chestPrizeLoading: false,
			chestPrizeMatchMissing: false,
			hidden: false,
			showInBanner: false,
			loaded: true,
			show: false,
		});
	}

	loadAvailableGames(provider) {
		if (provider === 'endorphina') {
			const formattedGames = this.getAvailableEndorphinaGames();

			if (!formattedGames.length) {
				this.props.Interface.addNoty("Error", "Please reload page game state not loading");
				return;
			}

			this.props.setPrizeEditModal(prevState => ({
				...prevState,
				availableGames: formattedGames
			}));
		}
	}

	getAvailableEndorphinaGames = () => {
		const gameState = this.props.game;
		if (!gameState?.loaded || !gameState?.data) {
			return [];
		}

		return gameState.data
			.filter((game) => game.providerID === 10 && game.active === 1)
			.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));
	};

	getFilteredGames() {
		const games = this.props.prizeEditModal.availableGames || [];

		return games.map(game => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game
		}));
	}

	getChestRewardGames = () =>
		this.getAvailableEndorphinaGames().map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));

	parseRewardConditions = (rewardConditions) => {
		if (!rewardConditions) {
			return {};
		}

		if (typeof rewardConditions === "string") {
			try {
				return JSON.parse(rewardConditions || "{}");
			} catch (error) {
				console.error("Failed to parse rewardConditions", error);
				return {};
			}
		}

		return rewardConditions;
	};

	buildFreespinRewardConditions = ({ gameSelected, betAmount, expiresAt }) => {
		const parsedBetAmount = parseFloat(betAmount) || 0;

		return {
			gameId: gameSelected?.gameId,
			providerId: gameSelected?.game?.providerID,
			betAmount: parsedBetAmount,
			currency: "RUB",
			expiresAt: parseInt(expiresAt),
		};
	};

	buildChestRewardsPayload = () => getChestRewardsPayload(this.props.prizeEditModal.chestRewards || []);

	loadChestPrizeOptions = async ({ matchCurrent = false } = {}) => {
		try {
			this.props.setPrizeEditModal((prevState) => ({
				...prevState,
				chestPrizeLoading: true,
			}));

			const response = await this.props.Interface.getChestPrizeOptions({ page: 1, limit: 999 });
			const items = response?.items || [];
			const options = items.map((item) => buildChestPrizeOption(item));
			const patch = {
				chestPrizeOptions: options,
				chestPrizeLoading: false,
			};

			if (matchCurrent) {
				const match = this.props.prizeEditModal.chestPrizeId
					? items.find((item) => Number(item.id) === Number(this.props.prizeEditModal.chestPrizeId)) || null
					: findMatchingChestPrize(items, {
							type: this.props.prizeEditModal.chestType,
							rewardConditions: this.props.prizeEditModal.chestRewards,
					  });

				patch.chestPrizeSelected = match
					? buildChestPrizeOption(match)
					: this.props.prizeEditModal.chestType || (this.props.prizeEditModal.chestRewards || []).length
					? {
							value: "__unmatched__",
							label: "Custom / unmatched chest",
							description: formatChestPrizeType(this.props.prizeEditModal.chestType),
					  }
					: null;
				patch.chestPrizeMatchMissing = !match && Boolean(this.props.prizeEditModal.chestType || (this.props.prizeEditModal.chestRewards || []).length);
			}

			this.props.setPrizeEditModal((prevState) => ({
				...prevState,
				...patch,
			}));
		} catch (error) {
			console.error(error);
			this.props.setPrizeEditModal((prevState) => ({
				...prevState,
				chestPrizeLoading: false,
				chestPrizeMatchMissing:
					matchCurrent &&
					Boolean(this.props.prizeEditModal.chestType || (this.props.prizeEditModal.chestRewards || []).length),
			}));
			this.props.Interface.addNoty("Error", "Failed to load chest presets");
		}
	};

	selectChestPrize = (selectedOption) => {
		const chestPrize = selectedOption?.chestPrize || null;

		this.props.setPrizeEditModal({
			...this.props.prizeEditModal,
			chestPrizeSelected: selectedOption,
			chestPrizeId: chestPrize?.id || null,
			chestType: chestPrize?.type || "",
			chestRewards: getChestRewardsPayload(chestPrize?.rewardConditions),
			chestPrizeMatchMissing: false,
		});
	};

	handleIconChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/svg+xml" || file.type === "image/png")) {
			if (this.props.prizeEditModal.icon) {
				URL.revokeObjectURL(this.props.prizeEditModal.icon);
			}
			const imageUrl = URL.createObjectURL(file);
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				icon: imageUrl,
				iconFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid SVG or PNG" });
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				icon: "",
				iconFile: null,
			});
		}
	};


	handleImageChange = (event) => {
		const file = event.target.files[0];
		this.setState({ imageError: "" });

		if (file && (file.type === "image/png")) {
			if (this.props.prizeEditModal.image) {
				URL.revokeObjectURL(this.props.prizeEditModal.image);
			}
			const imageUrl = URL.createObjectURL(file);
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				image: imageUrl,
				imageFile: file,
			});
		} else {
			this.setState({ imageError: "Please select a valid PNG file" });
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				image: "",
				imageFile: null,
			});
		}
	};

	fileToBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
			reader.readAsDataURL(file);
		});
	};

	validation() {
		try {
			if (this.props.prizeEditModal.rewardType?.value === "freespins") {
					if (!this.props.prizeEditModal.providerSelected) {
						return "Provider for freespins is required";
					}
					if (!this.props.prizeEditModal.gameSelected) {
						return "Game for freespins is required";
					}
					if (!this.props.prizeEditModal.betAmount) {
						return "Bet amount for freespins is required";
					}
					if (!this.props.prizeEditModal.expiresAt) {
						return "ExpiresAt for freespins is required";
					}

					const spinsCount = parseInt(this.props.prizeEditModal.rewardValue) || 0;
					if (!this.props.prizeEditModal.rewardValue || spinsCount < 1 || spinsCount > 1000) {
						return "Please enter rewardValue count (1-1000)";
					}

					const betAmount = parseFloat(this.props.prizeEditModal.betAmount) || 0;
					if (betAmount <= 0) {
						return "Please enter bet amount";
					}
				} else if (this.props.prizeEditModal.rewardType?.value === "chest") {
					if (!this.props.prizeEditModal.chestPrizeId) {
						return "Chest preset is required";
					}

				} else if (!this.props.prizeEditModal.rewardType?.value) {
					return "Reward type is required";
			}

			const hasCohortCondition = (this.props.prizeEditModal.conditions || []).some(
				(condition) =>
					condition?.field &&
					condition?.value !== null &&
					condition?.value !== undefined &&
					condition?.value !== ""
			);

			if (this.props.prizeEditModal.hidden && !hasCohortCondition) {
				return "Hidden prize requires cohort settings";
			}

			if (
				this.props.prizeEditModal.rewardType?.value === "item" &&
				(this.props.prizeEditModal.conditions || []).length > 0 &&
				!hasValidItemUidCondition(this.props.prizeEditModal.conditions)
			) {
				return "Item prizes require a Profile / Id condition using = or IN.";
			}

			return "";
		}catch (e) {
			console.log("TVAR", e);
		}
	}

	isSubmitDisabled = () => {
		const { prizeEditModal } = this.props;
		const requiresRewardValue = prizeEditModal.rewardType?.value !== "chest";
		const requiresChestPreset = prizeEditModal.rewardType?.value === "chest";
		const requiresImage = prizeEditModal.rewardType?.value !== "chest";
		const requiresItemUidCondition =
			prizeEditModal.rewardType?.value === "item" &&
			(prizeEditModal.conditions || []).length > 0;

		return (
			!prizeEditModal.name ||
			!prizeEditModal.description ||
			!prizeEditModal.rewardType ||
			!prizeEditModal.poolCount ||
			!prizeEditModal.poolType ||
			!prizeEditModal.active ||
			(requiresImage && !prizeEditModal.image) ||
			(requiresRewardValue && !prizeEditModal.rewardValue) ||
			(requiresChestPreset && !prizeEditModal.chestPrizeId) ||
			(requiresItemUidCondition && !hasValidItemUidCondition(prizeEditModal.conditions)) ||
			Number(prizeEditModal.probability) < 0
		);
	};

	renderComponentFromType(groupId, type, specialType, cohortType = "visibility") {
		//count boolean date string

		const { newConditionKey } = this.getCohortStateKeys(cohortType);
		const newCondition = this.state[newConditionKey]?.[groupId] || {};

		if (type === "multiselect" && specialType === "riskModel") {
			return <Multiselect
				selectedOptions={getRiskModelSelectedOptions(newCondition?.value)}
				onChange={({ detail }) => this.updateNewCondition(groupId, { value: detail.selectedOptions.map((option) => option.value) }, cohortType)}
				options={RISK_MODEL_OPTIONS}
				placeholder="Select risk models"
				selectedAriaLabel="Selected"
			/>;
		} else if (type === "string") {
			return <Input
				placeholder="Enter value"
				value={newCondition?.value || ""}
				onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value }, cohortType)}
			/>
		} else if (type === "count") {
			return <Input
				placeholder="Enter value"
				type="number"
				value={newCondition?.value || ""}
				onChange={(e) => this.updateNewCondition(groupId, { value: e.detail.value }, cohortType)}
			/>
		} else if (type === "date") {
			return <DatePicker
				onChange={({ detail }) => {
					this.updateNewCondition(groupId, { value: detail.value }, cohortType);
				}}
				value={newCondition?.value || null}
				placeholder="Select a date"
			/>
		} else if (type === "select" && specialType === "boolean") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption }, cohortType)
				}
				options={[{ value: "1", label: "Required" }, { value: "0", label: "No" }]}
				placeholder="Select type"
			/>
		} else if (type === "select" && specialType === "cryptoOrFiat") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption }, cohortType)
				}
				options={[{ value: "crypto", label: "Crypto" }, { value: "fiat", label: "Fiat" }]}
				placeholder="Select type"
			/>
		} else if (type === "select" && specialType === "partnerPaymentType") {
			return <Select
				selectedOption={
					newCondition?.value
				}
				onChange={({ detail }) =>
					this.updateNewCondition(groupId, { value: detail.selectedOption })
				}
				options={PARTNER_PAYMENT_TYPE_OPTIONS}
				placeholder="Select payment type"
			/>
		}

		return null;
	}

	renderCohortEditor = ({ cohortType = "visibility", description = "" } = {}) => {
		const { conditionsKey, groupsKey, newConditionKey } = this.getCohortStateKeys(cohortType);
		const groups = this.state[groupsKey] || [];
		const conditions = this.props.prizeEditModal[conditionsKey] || [];

		return (
			<SpaceBetween size="l">
				{description && <Alert type="info">{description}</Alert>}
				<Box>
					<Button variant="primary" iconName="add-plus" onClick={() => this.addCohortGroup(cohortType)}>
						Add group
					</Button>
				</Box>
				{groups.map((group, groupIndex) => {
					const moduleOptions = this.getCohortModuleOptions();
					const logicalOperatorOptions = this.getLogicalOperatorOptions();
					const selectedModule = moduleOptions.find((o) => o.value === group.module) || null;
					const groupOperatorValue = groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";
					const selectedGroupLogicalOperator = logicalOperatorOptions.find((o) => o.value === groupOperatorValue) || null;
					const groupConditions = conditions.filter((c) => c.groupId === group.groupId).sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));
					const newCondition = this.state[newConditionKey]?.[group.groupId] || {};
					const selectedConditionLogicalOperator = logicalOperatorOptions.find((o) => o.value === (newCondition.logicalOperator || "AND")) || null;
					const fieldOptions = this.getCohortFieldOptionsByModule(group.module);
					const selectedFieldOption = newCondition.field ? { value: newCondition.field, label: newCondition.fieldLabel } : null;
					const selectedOperatorOption = newCondition.operator ? { value: newCondition.operator, label: newCondition.operator } : null;
					const isAddDisabled = !newCondition.field || this.isConditionValueEmpty(newCondition.value) || (!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

					return (
						<Container
							key={group.groupId}
							header={
								<Header
									variant="h3"
									actions={
										<Button variant="icon" iconName="delete-marker" onClick={() => this.removeCohortGroup(group.groupId, cohortType)}>
											Remove group
										</Button>
									}
								>
									{`Group ${groupIndex + 1}`}
								</Header>
							}
						>
							<SpaceBetween size="m">
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
									<FormField label="Module" stretch>
										<Select
											selectedOption={selectedModule}
											onChange={({ detail }) => this.changeCohortGroupModule(group.groupId, detail.selectedOption.value, cohortType)}
											options={moduleOptions}
											placeholder="Select module"
										/>
									</FormField>
									<FormField label="Group operator" stretch>
										<Select
											selectedOption={selectedGroupLogicalOperator}
											onChange={({ detail }) => {
												if (groupIndex === 0) return;
												this.updateCohortGroup(group.groupId, { groupLogicalOperator: detail.selectedOption.value }, cohortType);
											}}
											options={logicalOperatorOptions}
											placeholder="Select operator"
											disabled={groupIndex === 0}
										/>
									</FormField>
									<Box />
								</Grid>

								<Table
									columnDefinitions={[
										{ id: "logic", header: "Logic", cell: (item) => item.logicalOperator || "-" },
										{ id: "field", header: "Field", cell: (item) => item.fieldLabel || item.field },
										{ id: "operator", header: "Operator", cell: (item) => item.operator },
										{ id: "value", header: "Value", cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value },
										{
											id: "actions",
											header: "Control",
											cell: (item) => (
												<Button
													variant="icon"
													iconName="delete-marker"
													onClick={() => {
														const next = this.normalizeGroupConditions(conditions.filter((c) => c.id !== item.id), group.groupId);
														this.props.setPrizeEditModal({ ...this.props.prizeEditModal, [conditionsKey]: next });
													}}
												>
													Remove
												</Button>
											),
										},
									]}
									footer={
										<FormField stretch label="Add condition">
											<Grid gridDefinition={groupConditions.length ? [{ colspan: 2 }, { colspan: 4 }, { colspan: 2 }, { colspan: 2 }, { colspan: 2 }] : [{ colspan: 4 }, { colspan: 3 }, { colspan: 3 }, { colspan: 2 }]}>
												{groupConditions.length > 0 && (
													<Select
														selectedOption={selectedConditionLogicalOperator}
														placeholder="AND/OR"
														options={logicalOperatorOptions}
														onChange={({ detail }) => this.updateNewCondition(group.groupId, { logicalOperator: detail.selectedOption.value }, cohortType)}
													/>
												)}
												<Select
													selectedOption={selectedFieldOption}
													placeholder="Select field"
													options={fieldOptions}
													onChange={({ detail }) =>
														this.updateNewCondition(group.groupId, {
															field: detail.selectedOption.value,
															fieldLabel: detail.selectedOption.label,
															conditionalType: detail.selectedOption.type,
															specialType: detail.selectedOption.specialType,
															value: null,
															operator: detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect" ? "=" : null,
														}, cohortType)
													}
												/>
												<Select
													selectedOption={selectedOperatorOption}
													placeholder="Select operator"
													disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
													options={[
														{ value: "=", label: "=" },
														{ value: ">", label: ">" },
														{ value: "<", label: "<" },
														{ value: ">=", label: ">=" },
														{ value: "<=", label: "<=" },
														{ value: "IN", label: "IN" },
													]}
													onChange={({ detail }) => this.updateNewCondition(group.groupId, { operator: detail.selectedOption.value }, cohortType)}
												/>
												<Box>{this.renderComponentFromType(group.groupId, newCondition.conditionalType, newCondition.specialType, cohortType)}</Box>
												<Button disabled={isAddDisabled} onClick={() => this.addCohortCondition(group, groupIndex, cohortType)}>
													+ Add
												</Button>
											</Grid>
										</FormField>
									}
									items={groupConditions}
									empty="No conditions set"
								/>
							</SpaceBetween>
						</Container>
					);
				})}
			</SpaceBetween>
		);
	};

	addPrize = async () => {
		try {
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				loaded: false,
			});

			let imageBase64 = "";
			let imageExtension = "";
			let iconBase64 = "";
			let iconExtension = "";

			if (this.props.prizeEditModal.imageFile) {
				try {
					imageBase64 = await this.fileToBase64(
						this.props.prizeEditModal.imageFile
					);
					imageExtension =
						this.props.prizeEditModal.imageFile.type === "image/png"
							? "png"
							: "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setPrizeEditModal({
						...this.props.prizeEditModal,
						loaded: true,
					});
					return;
				}
			}


			if (this.props.prizeEditModal.iconFile) {
				try {
					iconBase64 = await this.fileToBase64(
						this.props.prizeEditModal.iconFile
					);
					iconExtension =
						this.props.prizeEditModal.iconFile.type === "image/png"
							? "png"
							: "svg";
				} catch (error) {
					console.error("Failed to convert image to base64:", error);
					this.props.Interface.addNoty("Error", "Failed to process image");
					this.props.setPrizeEditModal({
						...this.props.prizeEditModal,
						loaded: true,
					});
					return;
				}
			}


			const buildRewardConditions = () => {
				const type = this.props.prizeEditModal.rewardType?.value;
				const hidden = Boolean(this.props.prizeEditModal.hidden);
				const showInBanner = Boolean(this.props.prizeEditModal.showInBanner);

				if (type === "chest") {
					return {
						chestPrizeId: Number(this.props.prizeEditModal.chestPrizeId),
						hidden,
						showInBanner,
					};
				}

				if (type === "freespins") {
					return {
						...this.buildFreespinRewardConditions({
							gameSelected: this.props.prizeEditModal.gameSelected,
							betAmount: this.props.prizeEditModal.betAmount,
							expiresAt: this.props.prizeEditModal.expiresAt,
						}),
						hidden,
						showInBanner,
					};
				}

				return { hidden, showInBanner };
			}


			const validation = this.validation();

			if (validation?.length) {
				this.props.Interface.addNoty("Error", validation);
				this.props.setPrizeEditModal({
					...this.props.prizeEditModal,
					loaded: true,
				});
				return;
			}

			const fakeWinsPerMonthValue = parseInt(this.props.prizeEditModal.fakeWinsPerMonth, 10);
			const fakeWinsPerMonth = Number.isNaN(fakeWinsPerMonthValue) ? null : fakeWinsPerMonthValue;
			const isChestPrize = this.props.prizeEditModal.rewardType?.value === "chest";

			const payload = {
				id: this.props.prizeEditModal.id,
				name: this.props.prizeEditModal.name,
				rewardType: this.props.prizeEditModal.rewardType.value,
				type: this.props.prizeEditModal.rewardType?.value === "chest" ? this.props.prizeEditModal.chestType : undefined,
				description: this.props.prizeEditModal.description,
				poolType: this.props.prizeEditModal.poolType.value,
				poolCount: parseInt(this.props.prizeEditModal.poolCount),
				rewardValue: this.props.prizeEditModal.rewardType?.value === "chest" ? 0 : parseInt(this.props.prizeEditModal.rewardValue),
				image: imageBase64 || this.props.prizeEditModal.image || "",
				imageExtension: imageBase64 ? imageExtension : "",
				icon: isChestPrize ? "" : iconBase64,
				iconExtension: isChestPrize ? "" : iconExtension,
				cohort: this.props.prizeEditModal.conditions,
				nameTranslationType: this.props.prizeEditModal.nameTranslationType,
				descriptionTranslationType: this.props.prizeEditModal.descriptionTranslationType,
				probability: this.props.prizeEditModal.probability,
				active: parseInt(this.props.prizeEditModal.active.value),
				rewardConditions: buildRewardConditions(),
				order: this.props.prizeEditModal.order,
				fakeWinsPerMonth,
				showInBanner: this.props.prizeEditModal.showInBanner ? 1 : 0,
			};

			this.props.Interface.request(
				"set",
				{ class: "data", module: "prize", method: "edit" },
				payload
			)
				.then(() => {
					this.props.Interface.addNoty(
						"Success",
						`Prize "${this.props.prizeEditModal.name}" was successfully edit`
					);

					if (this.props.prizeEditModal.image) {
						URL.revokeObjectURL(this.props.prizeEditModal.image);
					}

					this.props.setPrizeEditModal({
						...this.props.prizeEditModal,
						imageFile: null,
						iconFile: null,
						fakeWinsPerMonth: "",
						chestRewards: [],
						chestType: "",
						chestPrizeId: null,
						chestPrizeSelected: null,
						chestPrizeOptions: [],
						chestPrizeLoading: false,
						chestPrizeMatchMissing: false,
						hidden: false,
						showInBanner: false,
						show: false,
					});
					this.setState({ imageError: "" });

					this.props.Interface.loadPrizeList();
				})
				.catch((error) => {
					console.error("Failed to add prize:", error);
					this.props.setPrizeEditModal({
						...this.props.prizeEditModal,
						loaded: true,
					});
					this.props.Interface.addNoty("Error", "Failed to edit prize");
				});
		} catch (e) {
			console.log(e);
			this.props.Interface.addNoty("Error", e.text);
			this.props.setPrizeEditModal({
				...this.props.prizeEditModal,
				loaded: true,
			});
		}
	};

	render() {
		const isChestPrize = this.props.prizeEditModal.rewardType?.value === "chest";
		const chestBannerUrl = getChestBannerUrl(this.state.cdnUrl, this.props.prizeEditModal.chestType);
		const bannerPreviewUrl = this.props.prizeEditModal.image || (isChestPrize ? chestBannerUrl : "");

		return (
			<Modal
				visible={this.props.prizeEditModal.show}
				onDismiss={() => {
					if (this.props.prizeEditModal.image) {
						URL.revokeObjectURL(this.props.prizeEditModal.image);
					}
					this.onDismiss();
					this.setState({ imageError: "", levelError: "" });
				}}
				header={`Edit Prize ${this.props.prizeEditModal.id}`}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								variant="link"
								onClick={() => {
									this.onDismiss();
								}}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								iconName="check"
								disabled={this.isSubmitDisabled()}
								onClick={this.addPrize}
								loading={!this.props.prizeEditModal.loaded}
							>
								Edit Prize
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				{this.props.prizeEditModal.loaded ? (
					<Form>
						{this.state.imageError && (
							<Alert
								type="error"
								dismissible
								onDismiss={() => this.setState({ imageError: "" })}
							>
								{this.state.imageError}
							</Alert>
						)}

						<Tabs tabs={[
							{
								id: "general",
								label: "General",
								content:<SpaceBetween direction="vertical" size="l">
									<Container header={<Box variant="h3">General</Box>}>
										<SpaceBetween direction="vertical" size="m">
											<FormField label="Prize Name" stretch>
												<Input
													placeholder="Enter prize name"
													value={this.props.prizeEditModal.name}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															name: e.detail.value,
														})
													}
												/>
											</FormField>

											<FormField label="Name translation type" stretch>
												<Select
													selectedOption={this.props.prizeEditModal.nameTranslationType ? { value: this.props.prizeEditModal.nameTranslationType, label: this.props.prizeEditModal.nameTranslationType.charAt(0).toUpperCase() + this.props.prizeEditModal.nameTranslationType.slice(1) } : null}
													onChange={(e) => {
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															nameTranslationType: e.detail.selectedOption.value,
														})
													}}
													options={[
														{ value: "local", label: "Local" },
														{ value: "international", label: "International" },
														{ value: "mixed", label: "Mixed" },
													]}
													placeholder="Select prize name translation type"
												/>
											</FormField>

											<FormField label="Description" stretch>
												<Input
													placeholder="Enter description"
													value={this.props.prizeEditModal.description}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															description: e.detail.value,
														})
													}
												/>
											</FormField>

											<FormField label="Description translation type" stretch>
												<Select
													selectedOption={this.props.prizeEditModal.descriptionTranslationType ? { value: this.props.prizeEditModal.descriptionTranslationType, label: this.props.prizeEditModal.descriptionTranslationType.charAt(0).toUpperCase() + this.props.prizeEditModal.descriptionTranslationType.slice(1) } : null}
													onChange={(e) => {
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															descriptionTranslationType: e.detail.selectedOption.value,
														})
													}}
													options={[
														{ value: "local", label: "Local" },
														{ value: "international", label: "International" },
														{ value: "mixed", label: "Mixed" },
													]}
													placeholder="Select prize descritpion translation type"
												/>
											</FormField>


											<FormField label="Reward type" stretch>
												<Select
													selectedOption={this.props.prizeEditModal.rewardType}
													onChange={({ detail }) => {
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															rewardType: detail.selectedOption,
															probability: this.props.prizeEditModal.probability,
															image: detail.selectedOption.value !== "chest" ? null : this.props.prizeEditModal.image,
															imageFile: detail.selectedOption.value !== "chest" ? null : this.props.prizeEditModal.imageFile,
															gameSelected: null,
															availableGames: [],
															providerSelected: null,
															rewardValue: "",
															betAmount: "",
															expiresAt: "",
															chestRewards: [],
															chestType: "",
															chestPrizeId: null,
															chestPrizeSelected: null,
															chestPrizeOptions: detail.selectedOption.value === "chest" ? this.props.prizeEditModal.chestPrizeOptions : [],
															chestPrizeLoading: false,
															chestPrizeMatchMissing: false,
														});

														if (detail.selectedOption.value === "chest") {
															this.loadChestPrizeOptions();
														}
													}}
													options={this.props.prizeEditModal.currentOption.type}
													placeholder="Select a reward type"
												/>
											</FormField>
										{this.props.prizeEditModal.rewardType?.value !== "chest" && (
											<FormField label="Reward value" stretch>
												<Input
													type="number"
													placeholder="Enter reward value"
													value={this.props.prizeEditModal.rewardValue}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															rewardValue: e.detail.value,
														})
													}
												/>
											</FormField>
										)}
											<FormField label="Probability" stretch>
												<Input
													type="number"
													placeholder="Enter probability value"
													value={this.props.prizeEditModal.probability}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															probability: e.detail.value,
														})
													}
												/>
											</FormField>
											<FormField label="Order" stretch>
												<Input
													type="number"
													placeholder="Enter order value"
													value={this.props.prizeEditModal.order}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															order: e.detail.value,
														})
													}
												/>
											</FormField>
											<FormField label="Fake wins per month" stretch>
												<Input
													type="number"
													placeholder="Enter fake wins per month"
													value={this.props.prizeEditModal.fakeWinsPerMonth ?? ""}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															fakeWinsPerMonth: e.detail.value,
														})
													}
												/>
											</FormField>
											<FormField stretch>
												<Checkbox
													checked={Boolean(this.props.prizeEditModal.hidden)}
													onChange={({ detail }) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															hidden: detail.checked,
														})
													}
												>
													Hidden prize
												</Checkbox>
											</FormField>
											<FormField stretch>
												<Checkbox
													checked={Boolean(this.props.prizeEditModal.showInBanner)}
													onChange={({ detail }) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															showInBanner: detail.checked,
														})
													}
												>
													Show in banner
												</Checkbox>
											</FormField>
										</SpaceBetween>
									</Container>

									{this.props.prizeEditModal.rewardType?.value === "freespins" && <Container header={<Box variant="h3">Parameters</Box>}>
										<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }, { colspan: 6 }, { colspan: 6 },  { colspan: 6 },  { colspan: 6 },   { colspan: 6 }, { colspan: 6 }, { colspan: 6 },  { colspan: 6 }, { colspan: 12 },]}>
											<FormField label="Provider">
												<Select
													filteringType="auto"
													placeholder="Select Provider"
													selectedOption={this.props.prizeEditModal.providerSelected}
													onChange={(option) => {
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															providerSelected: option.detail.selectedOption,
															gameSelected: null,
															availableGames: [],
														});

														this.loadAvailableGames(option.detail.selectedOption.value);
													}}
												options={FREESPINS_PROVIDER_OPTIONS}
												/>
											</FormField>

											{this.props.prizeEditModal.providerSelected && (
												<>
													<FormField label="Game">
														<Select
															filteringType="auto"
															placeholder="Select Game (type to search...)"
															selectedOption={this.props.prizeEditModal.gameSelected}
															onChange={(option) => {
																this.props.setPrizeEditModal({
																	...this.props.prizeEditModal,
																	gameSelected: option.detail.selectedOption
																});
															}}
															options={this.getFilteredGames()}
														/>
													</FormField>

													<FormField label="Bet Amount">
														<Input
															type="number"
															inputMode="decimal"
															placeholder="Enter bet amount (e.g., 100 for 100.00 RUB)"
															value={this.props.prizeEditModal.betAmount || ""}
															onChange={(input) => {
																this.props.setPrizeEditModal({
																	...this.props.prizeEditModal,
																	betAmount: input.detail.value
																});
															}}
														/>
													</FormField>

													<FormField label="Expiration Date">
														<Input
															placeholder="Enter expiration date"
															value={this.props.prizeEditModal.expiresAt || ""}
															onChange={(input) => {
																this.props.setPrizeEditModal({
																	...this.props.prizeEditModal,
																	expiresAt: input.detail.value
																});
															}}
														/>
													</FormField>
												</>

											)}
										</Grid>
									</Container>}

								{this.props.prizeEditModal.rewardType?.value === "chest" && (
									<Container header={<Header variant="h3">Chest preset</Header>}>
										<SpaceBetween direction="vertical" size="m">
											<FormField label="Chest" stretch>
												<Select
													filteringType="auto"
													placeholder="Select chest preset"
													selectedOption={this.props.prizeEditModal.chestPrizeSelected}
													onChange={({ detail }) => this.selectChestPrize(detail.selectedOption)}
													options={this.props.prizeEditModal.chestPrizeOptions}
													statusType={this.props.prizeEditModal.chestPrizeLoading ? "loading" : "finished"}
													loadingText="Loading chest presets"
													empty="No chest presets found"
												/>
											</FormField>

											{this.props.prizeEditModal.chestPrizeMatchMissing && (
												<Alert type="warning">
													Saved chest config does not match any preset. Select a chest to replace it.
												</Alert>
											)}

											{this.props.prizeEditModal.chestType && (
												<Box>{`Chest type: ${formatChestPrizeType(this.props.prizeEditModal.chestType)}`}</Box>
											)}

											{(this.props.prizeEditModal.chestRewards || []).length > 0 && (
												<Box>{`Rewards in chest: ${(this.props.prizeEditModal.chestRewards || []).length}`}</Box>
											)}
										</SpaceBetween>
									</Container>
								)}


									<Container header={<Box variant="h3">Pool settings</Box>}>
										<Grid gridDefinition={[{ colspan: 12 }, { colspan: 12 }]}>
											<FormField label="Type" stretch>
												<Select
													selectedOption={
														this.props.prizeEditModal.poolType
													}
													onChange={({ detail }) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															poolType: detail.selectedOption,
														})
													}
													options={this.props.prizeEditModal.currentOption.poolType}
													placeholder="Select pool type"
												/>
											</FormField>
											<FormField label="Count" stretch>
												<Input
													type="number"
													placeholder="Enter count"
													value={this.props.prizeEditModal.poolCount}
													onChange={(e) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															poolCount: e.detail.value,
														})
													}
												/>
											</FormField>
										</Grid>
									</Container>


									<Container header={<Box variant="h3">Other</Box>}>
										<Grid gridDefinition={isChestPrize ? [{ colspan: 12 }, { colspan: 12 }] : [{ colspan: 12 }, { colspan: 6 }, { colspan: 6 }]}>
											<FormField label="Active" stretch>
												<Select
													selectedOption={
														this.props.prizeEditModal.active
													}
													onChange={({ detail }) =>
														this.props.setPrizeEditModal({
															...this.props.prizeEditModal,
															active: detail.selectedOption,
														})
													}
													options={[{ value: "1", label: "Enabled" }, { value: "0", label: "Disabled" }]}
													placeholder="Select active"
												/>
											</FormField>
											<>
												<FormField
													label="Banner image"
													description={isChestPrize ? "Upload PNG image or use default chest banner" : "Upload PNG image"}
													stretch
												>
													<input
														type="file"
														accept="image/png"
														onChange={this.handleImageChange}
													/>
													{bannerPreviewUrl && (
														<Box margin={{ top: "s" }} textAlign="center">
															<img
																src={bannerPreviewUrl}
																alt={isChestPrize && !this.props.prizeEditModal.image ? "chest banner preview" : "prize preview"}
																style={{
																	maxWidth: "100%",
																	maxHeight: "150px",
																	border: "1px solid #d5dbdb",
																	borderRadius: "4px",
																	padding: "4px",
																	backgroundColor: "#fafafa",
																}}
															/>
														</Box>
													)}
												</FormField>

												{!isChestPrize && (
													<FormField
														label="Icon"
														description="Upload an SVG or PNG image"
														stretch
													>
														<input
															type="file"
															accept="image/svg+xml,image/png"
															onChange={this.handleIconChange}
														/>
														{this.props.prizeEditModal.icon && (
															<Box margin={{ top: "s" }} textAlign="center">
																<img
																	src={this.props.prizeEditModal.icon}
																	alt="icon preview"
																	style={{
																		maxWidth: "100%",
																		maxHeight: "150px",
																		border: "1px solid #d5dbdb",
																		borderRadius: "4px",
																		padding: "4px",
																		backgroundColor: "#fafafa",
																	}}
																/>
															</Box>
														)}
													</FormField>
												)}
											</>
										</Grid>
									</Container>

								</SpaceBetween>
							},
							{
								id: "cohort",
								label: "Cohort Settings",
								content: (
									<SpaceBetween size="l">
										{this.props.prizeEditModal.rewardType?.value === "item" && (
											<Alert type="info">
												Without Cohort Settings, this item is display-only and cannot be won. If you add conditions, Item prizes require a Profile / Id condition using = or IN.
											</Alert>
										)}
										<Box>
											<Button
												variant="primary"
												iconName="add-plus"
												onClick={this.addCohortGroup}
											>
												Add group
											</Button>
										</Box>
										{(this.state.cohortGroups || []).map((group, groupIndex) => {
											const moduleOptions = this.getCohortModuleOptions();
											const logicalOperatorOptions = this.getLogicalOperatorOptions();

											const selectedModule =
												moduleOptions.find((o) => o.value === group.module) || null;

											const groupOperatorValue =
												groupIndex === 0 ? "AND" : group.groupLogicalOperator || "AND";

											const selectedGroupLogicalOperator =
												logicalOperatorOptions.find((o) => o.value === groupOperatorValue) ||
												null;

											const groupConditions = (this.props.prizeEditModal.conditions || [])
												.filter((c) => c.groupId === group.groupId)
												.sort((a, b) => (a.conditionOrder ?? 0) - (b.conditionOrder ?? 0));

											const newCondition =
												this.state.newConditionByGroupId?.[group.groupId] || {};

											const selectedConditionLogicalOperator =
												logicalOperatorOptions.find(
													(o) => o.value === (newCondition.logicalOperator || "AND")
												) || null;

											const fieldOptions = this.getCohortFieldOptionsByModule(group.module);

											const selectedFieldOption = newCondition.field
												? { value: newCondition.field, label: newCondition.fieldLabel }
												: null;

											const selectedOperatorOption = newCondition.operator
												? { value: newCondition.operator, label: newCondition.operator }
												: null;

											const isAddDisabled =
												!newCondition.field ||
												this.isConditionValueEmpty(newCondition.value) ||
												(!newCondition.operator && newCondition.conditionalType !== "select" && newCondition.conditionalType !== "multiselect");

											return (
												<Container
													key={group.groupId}
													header={
														<Header
															variant="h3"
															actions={
																<Button
																	variant="icon"
																	iconName="delete-marker"
																	onClick={() => this.removeCohortGroup(group.groupId)}
																>
																	Remove group
																</Button>
															}
														>
															{`Group ${groupIndex + 1}`}
														</Header>
													}
												>
									<SpaceBetween size="m">
														<Grid
															gridDefinition={[
																{ colspan: 4 },
																{ colspan: 4 },
																{ colspan: 4 },
															]}
														>
															<FormField label="Module" stretch>
																<Select
																	selectedOption={selectedModule}
																	onChange={({ detail }) =>
																		this.changeCohortGroupModule(
																			group.groupId,
																			detail.selectedOption.value
																		)
																	}
																	options={moduleOptions}
																	placeholder="Select module"
																/>
															</FormField>

															<FormField label="Group operator" stretch>
																<Select
																	selectedOption={selectedGroupLogicalOperator}
																	onChange={({ detail }) => {
																		if (groupIndex === 0) return;
																		this.updateCohortGroup(group.groupId, {
																			groupLogicalOperator: detail.selectedOption.value,
																		});
																	}}
																	options={logicalOperatorOptions}
																	placeholder="Select operator"
																	disabled={groupIndex === 0}
																/>
															</FormField>

															<Box />
														</Grid>

											<Table
												columnDefinitions={[
																{
																	id: "logic",
																	header: "Logic",
																	cell: (item) => item.logicalOperator || "-",
																},
																{
																	id: "field",
																	header: "Field",
																	cell: (item) => item.fieldLabel || item.field,
																},
																{
																	id: "operator",
																	header: "Operator",
																	cell: (item) => item.operator,
																},
																{
																	id: "value",
																	header: "Value",
																	cell: (item) => Array.isArray(item.value) ? item.value.join(", ") : item.value,
																},
													{
														id: "actions",
														header: "Control",
																	cell: (item) => (
															<Button
																variant="icon"
																iconName="delete-marker"
																onClick={() => {
																				const next = this.normalizeGroupConditions(
																					(this.props.prizeEditModal.conditions || []).filter(
																						(c) => c.id !== item.id
																					),
																					group.groupId
																				);
																	this.props.setPrizeEditModal({
																		...this.props.prizeEditModal,
																					conditions: next,
																	});
																}}
															>
																Remove
															</Button>
														),
													},
												]}
															footer={
																<FormField stretch label="Add condition">
																	<Grid
																		gridDefinition={
																			groupConditions.length
																				? [
																						{ colspan: 2 },
																						{ colspan: 4 },
																						{ colspan: 2 },
																						{ colspan: 2 },
																						{ colspan: 2 },
																				  ]
																				: [
																						{ colspan: 4 },
																						{ colspan: 3 },
																						{ colspan: 3 },
																						{ colspan: 2 },
																				  ]
																		}
																	>
																		{groupConditions.length > 0 && (
															<Select
																				selectedOption={selectedConditionLogicalOperator}
																				placeholder="AND/OR"
																				options={logicalOperatorOptions}
																				onChange={({ detail }) =>
																					this.updateNewCondition(group.groupId, {
																						logicalOperator: detail.selectedOption.value,
																					})
																				}
																			/>
																		)}

																		<Select
																			selectedOption={selectedFieldOption}
																placeholder="Select field"
																			options={fieldOptions}
																			onChange={({ detail }) =>
																				this.updateNewCondition(group.groupId, {
																			field: detail.selectedOption.value,
																			fieldLabel: detail.selectedOption.label,
																			conditionalType: detail.selectedOption.type,
																			specialType: detail.selectedOption.specialType,
																			value: null,
																					operator:
																						detail.selectedOption.type === "select" || detail.selectedOption.type === "multiselect"
																							? "="
																							: null,
																				})
																			}
															/>

															<Select
																			selectedOption={selectedOperatorOption}
																placeholder="Select operator"
																			disabled={newCondition.conditionalType === "select" || newCondition.conditionalType === "multiselect"}
																options={[
																	{ value: "=", label: "=" },
																	{ value: ">", label: ">" },
																	{ value: "<", label: "<" },
																	{ value: ">=", label: ">=" },
																	{ value: "<=", label: "<=" },
																	{ value: "IN", label: "IN" },
																]}
																onChange={({ detail }) =>
																				this.updateNewCondition(group.groupId, {
																			operator: detail.selectedOption.value,
																	})
																}
															/>

																		<Box>
																			{this.renderComponentFromType(
																				group.groupId,
																				newCondition.conditionalType,
																				newCondition.specialType
																			)}
																		</Box>

															<Button
																			disabled={isAddDisabled}
																			onClick={() => this.addCohortCondition(group, groupIndex)}
															>
																+ Add
															</Button>
														</Grid>
													</FormField>
															}
															items={groupConditions}
												empty="No conditions set"
											/>
									</SpaceBetween>
												</Container>
											);
										})}
									</SpaceBetween>
								)
								,
							},
						]} />
					</Form>
				) : (
					<Box textAlign="center">
						<Spinner size="large" />
					</Box>
				)}
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const prizeEditModal = useRecoilValue(prizeEditModalStore);
		const setPrizeEditModal = useSetRecoilState(prizeEditModalStore);
		const game = useRecoilValue(gameStore);

		return (
			<Component
				{...props}
				game={game}
				prizeEditModal={prizeEditModal}
				setPrizeEditModal={setPrizeEditModal}
			/>
		);
	};
}

export default withRecoil(PrizeEditModal);
